import React, { useRef } from 'react';
import {
  Avatar, AvatarFallbackText, AvatarImage, Box, Button, ButtonText,
  Divider, Heading, HStack, Input, InputField, ScrollView, Switch, Text, VStack,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

import ContentGroup from '@/common/components/ContentGroup';
import { PersonRow } from '@/common/components/people/PersonRow';

import type { ProfileFormState } from '../hooks/useProfileForm';

type ProfileScreenBodyProps = {
  form: ProfileFormState;
  updateField: <K extends keyof ProfileFormState>(key: K, value: ProfileFormState[K]) => void;
  emailError: string | null;
  photoUrl: string | null;
  hasStoredPhoto: boolean;
  initials: string;
  firstName: string;
  previewReach: { email?: string | null; phone?: string | null; whatsapp?: string | null };
  hasShareableContact: boolean;
  canOpenToConnect: boolean;
  isDirty: boolean;
  isSaving: boolean;
  onSave: () => void;
  onOpenWebcam: () => void;
  onRemovePhoto: () => void;
  onPickFile: (file: File) => void;
};

/**
 * ProfileScreenBody
 * ----
 * Student people profile sections and anchored save bar.
 */
export function ProfileScreenBody({
  form,
  updateField,
  emailError,
  photoUrl,
  hasStoredPhoto,
  initials,
  firstName,
  previewReach,
  hasShareableContact,
  canOpenToConnect,
  isDirty,
  isSaving,
  onSave,
  onOpenWebcam,
  onRemovePhoto,
  onPickFile,
}: ProfileScreenBodyProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const displayPhoto = form.showPhotoToConnections && !!photoUrl;

  const openFilePicker = () => fileInputRef.current?.click();

  return (
    <Box flex={1}>
      <ScrollView flex={1} testID="profile-form-scroll">
        <VStack
          space="2xl"
          px="$4"
          py="$6"
          maxWidth={800}
          alignSelf="center"
          width="100%"
          pb="$4"
        >
          <ContentGroup variant="bordered" accentColor="$brandBase" p="$4">
            <Heading size="lg">{t('people.profile.photo.title')}</Heading>
            <Text size="sm" color="$textSubtle">{t('people.profile.photo.help')}</Text>
            <HStack space="lg" alignItems="center">
              <Avatar size="2xl" bg="$brandBase">
                {photoUrl && form.showPhotoToConnections ? (
                  <AvatarImage source={{ uri: photoUrl }} alt="" />
                ) : (
                  <AvatarFallbackText color="$textOnBrand">{initials}</AvatarFallbackText>
                )}
              </Avatar>
              <VStack space="sm">
                <Button variant="outline" onPress={openFilePicker} testID="profile-upload-photo">
                  <ButtonText>
                    {hasStoredPhoto ? t('people.profile.photo.replace') : t('people.profile.photo.upload')}
                  </ButtonText>
                </Button>
                <Button variant="outline" onPress={onOpenWebcam} testID="profile-use-webcam">
                  <ButtonText>{t('people.profile.photo.webcam')}</ButtonText>
                </Button>
                {hasStoredPhoto && (
                <Button variant="link" onPress={onRemovePhoto} testID="profile-remove-photo">
                  <ButtonText>{t('people.profile.photo.remove')}</ButtonText>
                </Button>
                )}
              </VStack>
            </HStack>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ display: 'none' }}
              data-testid="profile-file-input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onPickFile(file);
                e.target.value = '';
              }}
            />
            <Divider bg="$borderLight" />
            <HStack alignItems="center" justifyContent="space-between">
              <Text flex={1} pr="$3">{t('people.profile.photo.showToConnections')}</Text>
              <Switch
                value={form.showPhotoToConnections}
                onValueChange={(v) => updateField('showPhotoToConnections', v)}
                testID="profile-show-photo-switch"
              />
            </HStack>
          </ContentGroup>

          <ContentGroup variant="bordered" accentColor="$brandBase" p="$4">
            <Heading size="lg">{t('people.profile.contacts.title')}</Heading>
            <Text size="sm" color="$textSubtle">{t('people.profile.contacts.help')}</Text>
            <VStack space="md">
              <VStack space="xs">
                <Text size="sm" fontWeight="medium">{t('people.profile.contacts.email.label')}</Text>
                <Input borderColor={emailError ? '$statusError' : '$borderMedium'}>
                  <InputField
                    value={form.shareEmail}
                    onChangeText={(v) => updateField('shareEmail', v)}
                    placeholder={t('people.profile.contacts.email.placeholder')}
                    testID="profile-share-email"
                  />
                </Input>
                <Text size="xs" color="$textSubtle">{t('people.profile.contacts.email.help')}</Text>
                {!!emailError && <Text size="xs" color="$statusError">{emailError}</Text>}
              </VStack>
              <VStack space="xs">
                <Text size="sm" fontWeight="medium">{t('people.profile.contacts.phone.label')}</Text>
                <Input>
                  <InputField
                    value={form.phone}
                    onChangeText={(v) => updateField('phone', v)}
                    placeholder={t('people.profile.contacts.phone.placeholder')}
                    keyboardType="phone-pad"
                    testID="profile-phone"
                  />
                </Input>
                <HStack alignItems="center" justifyContent="space-between">
                  <Text flex={1}>{t('people.profile.contacts.share')}</Text>
                  <Switch
                    value={form.sharePhone}
                    onValueChange={(v) => updateField('sharePhone', v)}
                    isDisabled={!form.phone.trim()}
                    testID="profile-share-phone"
                  />
                </HStack>
              </VStack>
              <VStack space="xs">
                <Text size="sm" fontWeight="medium">{t('people.profile.contacts.whatsapp.label')}</Text>
                <Input>
                  <InputField
                    value={form.whatsapp}
                    onChangeText={(v) => updateField('whatsapp', v)}
                    placeholder={t('people.profile.contacts.whatsapp.placeholder')}
                    keyboardType="phone-pad"
                    testID="profile-whatsapp"
                  />
                </Input>
                <HStack alignItems="center" justifyContent="space-between">
                  <Text flex={1}>{t('people.profile.contacts.share')}</Text>
                  <Switch
                    value={form.shareWhatsapp}
                    onValueChange={(v) => updateField('shareWhatsapp', v)}
                    isDisabled={!form.whatsapp.trim()}
                    testID="profile-share-whatsapp"
                  />
                </HStack>
              </VStack>
            </VStack>
          </ContentGroup>

          <ContentGroup variant="bordered" accentColor="$brandBase" p="$4">
            <Heading size="lg">{t('people.profile.gss.title')}</Heading>
            <HStack alignItems="center" justifyContent="space-between">
              <VStack flex={1} pr="$3" space="xs">
                <Text color={canOpenToConnect ? '$textBody' : '$textDisabled'}>
                  {t('people.profile.gss.toggle')}
                </Text>
                <Text size="sm" color="$textSubtle">
                  {!canOpenToConnect
                    ? t('people.profile.gss.helpLocked')
                    : form.openToConnectGss
                      ? t('people.profile.gss.helpOn')
                      : t('people.profile.gss.helpOff')}
                </Text>
              </VStack>
              <Switch
                value={canOpenToConnect && form.openToConnectGss}
                onValueChange={(v) => updateField('openToConnectGss', v)}
                isDisabled={!canOpenToConnect}
                testID="profile-gss-open-switch"
              />
            </HStack>
          </ContentGroup>

          <ContentGroup variant="bordered" accentColor="$brandBase" p="$4">
            <Heading size="lg">{t('people.profile.preview.title')}</Heading>
            <PersonRow
              firstName={firstName}
              initials={initials}
              photoUrl={photoUrl}
              showPhoto={displayPhoto}
              reach={previewReach}
              preview
            />
            {!hasShareableContact && (
            <Box
              mt="$3"
              bg="$statusWarningSubtle"
              borderLeftWidth={4}
              borderLeftColor="$statusWarning"
              p="$3"
              testID="profile-preview-warning"
            >
              <Text fontWeight="semibold" color="$textOnWarningSubtle">
                {t('people.profile.preview.emptyTitle')}
              </Text>
              <Text size="sm" color="$textOnWarningSubtle">{t('people.profile.preview.emptyBody')}</Text>
            </Box>
            )}
          </ContentGroup>
        </VStack>
      </ScrollView>

      <Box
        borderTopWidth={1}
        borderColor="$borderLight"
        bg="$bgCard"
        px="$4"
        py="$3"
        testID="profile-save-bar"
      >
        <Text
          size="sm"
          color={isDirty ? '$textOnWarningSubtle' : '$textSubtle'}
          mb="$2"
        >
          {isDirty ? t('people.profile.save.dirty') : t('people.profile.save.clean')}
        </Text>
        <Button action="primary" size="lg" onPress={onSave} isDisabled={isSaving} testID="profile-save-button">
          <ButtonText>{t('people.profile.save.cta')}</ButtonText>
        </Button>
      </Box>
    </Box>
  );
}
